import React, {useCallback, useEffect, useRef, useState} from 'react';
import root from 'window-or-global';
import throttle from 'lodash.throttle';

interface LicenseRefs {
    standard: {
        card: React.RefObject<HTMLDivElement>;
        title: React.RefObject<HTMLDivElement>;
        price: React.RefObject<HTMLDivElement>;
        includes: React.RefObject<HTMLDivElement>;
        bestFor: React.RefObject<HTMLDivElement>;
    };
    enterprise: {
        title: React.RefObject<HTMLDivElement>;
        price: React.RefObject<HTMLDivElement>;
        includes: React.RefObject<HTMLDivElement>;
        bestFor: React.RefObject<HTMLDivElement>;
    };
    custom: {
        title: React.RefObject<HTMLDivElement>;
        price: React.RefObject<HTMLDivElement>;
        includes: React.RefObject<HTMLDivElement>;
        bestFor: React.RefObject<HTMLDivElement>;
    };
}
interface LicensesRefsResponse {
    refs: LicenseRefs;
    gridRef: React.RefObject<HTMLDivElement>;
}

export const useLicensesRefs = (): LicensesRefsResponse => {
    // track the refs together in one object
    const refs: LicenseRefs = {
        standard: {
            card: useRef<HTMLDivElement>(null),
            title: useRef<HTMLDivElement>(null),
            price: useRef<HTMLDivElement>(null),
            includes: useRef<HTMLDivElement>(null),
            bestFor: useRef<HTMLDivElement>(null)
        },
        enterprise: {
            title: useRef<HTMLDivElement>(null),
            price: useRef<HTMLDivElement>(null),
            includes: useRef<HTMLDivElement>(null),
            bestFor: useRef<HTMLDivElement>(null)
        },
        custom: {
            title: useRef<HTMLDivElement>(null),
            price: useRef<HTMLDivElement>(null),
            includes: useRef<HTMLDivElement>(null),
            bestFor: useRef<HTMLDivElement>(null)
        }
    };

    const gridRef = useRef<HTMLDivElement>(null);

    return {
        refs,
        gridRef
    };
};

interface AlignLicensesResponse {
    standardWidth;
    titleHeight;
    priceHeight;
    includesHeight;
    bestForHeight;
}

export const useAlignLicenses = (
    isMd: boolean,
    refs: LicenseRefs,
    gridRef: React.RefObject<HTMLDivElement>
): AlignLicensesResponse => {
    const [standardWidth, setStandardWidth] = useState(0);
    const [titleHeight, setTitleHeight] = useState('initial');
    const [priceHeight, setPriceHeight] = useState('initial');
    const [includesHeight, setIncludesHeight] = useState('initial');
    const [bestForHeight, setBestForHeight] = useState('initial');

    /**
     * need to calculate the right width to set the standard card to and update it on resize
     * */
    const updateCardWidth = useCallback(() => {
        if (gridRef && gridRef.current && !isMd) {
            const {width} = gridRef.current.getBoundingClientRect();
            const newWidth = Math.floor(width / 3);
            if (standardWidth !== newWidth) {
                setStandardWidth(newWidth);
            }
        }
    }, [gridRef, standardWidth, setStandardWidth, isMd]);

    interface UpdateAtomHeightProps {
        isMd: boolean;
        atomHeight: string;
        setAtomHeight: React.Dispatch<React.SetStateAction<string>>;
        standardRef: React.RefObject<HTMLDivElement>;
        enterpriseRef: React.RefObject<HTMLDivElement>;
        customRef: React.RefObject<HTMLDivElement>;
    }

    const updateAtomHeight = ({
        isMd,
        atomHeight,
        setAtomHeight,
        standardRef,
        enterpriseRef,
        customRef
    }: UpdateAtomHeightProps) => {
        if (
            atomHeight === 'initial' &&
            !isMd &&
            standardRef?.current &&
            enterpriseRef?.current &&
            customRef?.current
        ) {
            const max = Math.max(
                standardRef.current.getBoundingClientRect().height,
                enterpriseRef.current.getBoundingClientRect().height,
                customRef.current.getBoundingClientRect().height
            );
            const pxHeight = `${max}px`;
            setAtomHeight(pxHeight);
        }
    };

    interface ResetAtomHeightProps {
        setAtomHeight: React.Dispatch<React.SetStateAction<string>>;
        standardRef: React.RefObject<HTMLDivElement>;
        enterpriseRef: React.RefObject<HTMLDivElement>;
        customRef: React.RefObject<HTMLDivElement>;
    }

    const resetAtomHeight = ({
        setAtomHeight,
        standardRef,
        enterpriseRef,
        customRef
    }: ResetAtomHeightProps) => {
        if (standardRef?.current && enterpriseRef?.current && customRef?.current) {
            setAtomHeight('initial');
        }
    };

    /**
     * Track the max height of the three title sections resetting on resize
     */
    const updateTitleHeight = useCallback(() => {
        updateAtomHeight({
            isMd,
            atomHeight: titleHeight,
            setAtomHeight: setTitleHeight,
            standardRef: refs.standard.title,
            enterpriseRef: refs.enterprise.title,
            customRef: refs.custom.title
        });
    }, [
        refs.standard.title,
        refs.enterprise.title,
        refs.custom.title,
        titleHeight,
        setTitleHeight,
        isMd
    ]);
    const resetTitleHeight = useCallback(() => {
        resetAtomHeight({
            setAtomHeight: setTitleHeight,
            standardRef: refs.standard.title,
            enterpriseRef: refs.enterprise.title,
            customRef: refs.custom.title
        });
    }, [refs.standard.title, refs.enterprise.title, refs.custom.title, setTitleHeight]);

    /**
     * Track the max height of the three Price sections resetting on resize
     */
    const updatePriceHeight = useCallback(() => {
        updateAtomHeight({
            isMd,
            atomHeight: priceHeight,
            setAtomHeight: setPriceHeight,
            standardRef: refs.standard.price,
            enterpriseRef: refs.enterprise.price,
            customRef: refs.custom.price
        });
    }, [
        refs.standard.price,
        refs.enterprise.price,
        refs.custom.price,
        priceHeight,
        setPriceHeight,
        isMd
    ]);
    const resetPriceHeight = useCallback(() => {
        resetAtomHeight({
            setAtomHeight: setPriceHeight,
            standardRef: refs.standard.price,
            enterpriseRef: refs.enterprise.price,
            customRef: refs.custom.price
        });
    }, [refs.standard.price, refs.enterprise.price, refs.custom.price, setPriceHeight]);

    /**
     * Track the max height of the three Includes sections resetting on resize
     */
    const updateIncludesHeight = useCallback(() => {
        updateAtomHeight({
            isMd,
            atomHeight: includesHeight,
            setAtomHeight: setIncludesHeight,
            standardRef: refs.standard.includes,
            enterpriseRef: refs.enterprise.includes,
            customRef: refs.custom.includes
        });
    }, [
        refs.standard.includes,
        refs.enterprise.includes,
        refs.custom.includes,
        includesHeight,
        setIncludesHeight,
        isMd
    ]);
    const resetIncludesHeight = useCallback(() => {
        resetAtomHeight({
            setAtomHeight: setIncludesHeight,
            standardRef: refs.standard.includes,
            enterpriseRef: refs.enterprise.includes,
            customRef: refs.custom.includes
        });
    }, [refs.standard.includes, refs.enterprise.includes, refs.custom.includes, setIncludesHeight]);

    /**
     * Track the max height of the three bestFor sections resetting on resize
     */
    const updateBestForHeight = useCallback(() => {
        updateAtomHeight({
            isMd,
            atomHeight: bestForHeight,
            setAtomHeight: setBestForHeight,
            standardRef: refs.standard.bestFor,
            enterpriseRef: refs.enterprise.bestFor,
            customRef: refs.custom.bestFor
        });
    }, [
        refs.standard.bestFor,
        refs.enterprise.bestFor,
        refs.custom.bestFor,
        bestForHeight,
        setBestForHeight,
        isMd
    ]);
    const resetBestForHeight = useCallback(() => {
        resetAtomHeight({
            setAtomHeight: setBestForHeight,
            standardRef: refs.standard.bestFor,
            enterpriseRef: refs.enterprise.bestFor,
            customRef: refs.custom.bestFor
        });
    }, [refs.standard.bestFor, refs.enterprise.bestFor, refs.custom.bestFor, setBestForHeight]);

    const updateSizes = () => {
        updateCardWidth();
        updateTitleHeight();
        updatePriceHeight();
        updateIncludesHeight();
        updateBestForHeight();
    };

    const resetSizes = () => {
        updateCardWidth();
        resetTitleHeight();
        resetPriceHeight();
        resetIncludesHeight();
        resetBestForHeight();
    };

    //ensure that the height is not set on md
    useEffect(() => {
        if (isMd) {
            if (titleHeight !== 'initial') setTitleHeight('initial');
            if (priceHeight !== 'initial') setPriceHeight('initial');
            if (includesHeight !== 'initial') setIncludesHeight('initial');
            if (bestForHeight !== 'initial') setBestForHeight('initial');
        }
    }, [
        titleHeight,
        priceHeight,
        includesHeight,
        bestForHeight,
        setTitleHeight,
        setPriceHeight,
        setIncludesHeight,
        setBestForHeight,
        isMd
    ]);

    const throttledResetSizes = throttle(resetSizes, 100, {leading: false});

    useEffect(() => {
        updateSizes();
        root.addEventListener('resize', throttledResetSizes);
        return () => {
            throttledResetSizes.cancel();
            root.removeEventListener('resize', throttledResetSizes);
        };
    }, [updateSizes, throttledResetSizes]);

    return {
        standardWidth,
        titleHeight,
        priceHeight,
        includesHeight,
        bestForHeight
    };
};
